import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import "./Help.css";
import Sidebar from "./Sidebar";
import NotificationBell from "./NotificationBell";
import ProfilePicture from "./ProfilePicture";
import iconMenu from "../assets/icon-menu.svg";
import iconSearch from "../assets/icon-search.svg";

const Help = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const containerClass = sidebarOpen
    ? "analytics-main-container"
    : "analytics-main-container sidebar-closed";

  const fetchProfile = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await axios.get(`${API_BASE_URL}/profile`, {
        params: { email: storedUser.email },
        withCredentials: true
      });
      setProfileData(response.data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const faqs = [
    {
      question: 'Why are my run locations blank?',
      answer: (
        <>
          <p>
            Location data is populated using a free geocoding service that is
            limited to approximately one lookup per second to respect their
            usage policy.
          </p>
          <p>
            <strong>What this means:</strong>
          </p>
          <ul>
            <li>
              <strong>Initial sync:</strong> After your first full re-sync,
              location data will populate gradually over 45-50 minutes for all
              historical activities.
            </li>
            <li>
              <strong>Ongoing syncs:</strong> New activities (typically 1-10
              per week) will have their locations populated within seconds.
            </li>
          </ul>
          <p>
            <strong>Note:</strong> Each activity's location is only geocoded
            once. Once populated, it won't change unless you manually re-sync.
          </p>
        </>
      ),
    },
    {
      question: "Do I need to do anything to get weather or location data?",
      answer: (
        <>
          <p>
            No extra steps needed. After a Strava sync, the dashboard
            automatically fills in weather and location for your runs. For a
            big first sync this can take a bit, but it completes in the
            background—just keep the tab open and check back.
          </p>
          <p>
            New activities get their weather and location shortly after they
            arrive from Strava.
          </p>
        </>
      ),
    },
    {
      question: "How do I sync my Strava activities?",
      answer: (
        <>
          <p>There are three ways to sync your activities from Strava:</p>
          <ul>
            <li>
              <strong>Automatic sync:</strong> Once connected, new activities
              are automatically synced within minutes of completing them on
              Strava (via webhooks).
            </li>
            <li>
              <strong>"Sync latest activities":</strong> Manually sync recent
              activities from the past 7 days (use this if you think something
              is missing).
            </li>
            <li>
              <strong>"Full re-sync":</strong> Sync all activities from the
              past 15 years. Use this for initial setup or if you need to
              refresh all data.
            </li>
          </ul>
          <p>
            Access these options by clicking the Strava icon in the top
            navigation bar.
          </p>
        </>
      ),
    },
    {
      question: "Can I upload activities manually (GPX/TCX files)?",
      answer: (
        <>
          <p>
            Yes! You can upload activities in GPX or TCX format directly from
            the Activities page.
          </p>
          <p>
            <strong>To upload:</strong>
          </p>
          <ol>
            <li>Navigate to the Activities page</li>
            <li>Click the "Upload GPX/TCX" button</li>
            <li>Select your file(s)</li>
            <li>Activities will be processed and added to your dashboard</li>
          </ol>
          <p>
            Manually uploaded activities will have location data populated
            immediately using the GPS coordinates in the file.
          </p>
        </>
      ),
    },
    {
      question: "How is my runstreak calculated?",
      answer: (
        <>
          <p>
            Your runstreak counts consecutive days with at least one running
            activity. The streak is calculated based on the activity's local
            start date.
          </p>
          <p>
            <strong>Key points:</strong>
          </p>
          <ul>
            <li>Only activities marked as "Run" on Strava count</li>
            <li>Multiple runs in one day still count as one day</li>
            <li>Missing a single day breaks the streak</li>
            <li>
              The streak counter shows both your current streak and your longest
              streak
            </li>
          </ul>
        </>
      ),
    },
    {
      question: "What data is stored from Strava?",
      answer: (
        <>
          <p>We sync and store the following data from your Strava activities:</p>
          <ul>
            <li>Activity name, date, and time</li>
            <li>Distance, duration, and pace</li>
            <li>Elevation gain and heart rate data (if available)</li>
            <li>GPS route data (polyline)</li>
            <li>Split times and pace zones</li>
            <li>Location (geocoded from GPS coordinates)</li>
          </ul>
          <p>
            We <strong>do not</strong> store your Strava password. Authentication
            uses OAuth tokens that can be revoked at any time from your Strava
            settings.
          </p>
        </>
      ),
    },
    {
      question: "How do I disconnect my Strava account?",
      answer: (
        <>
          <p>To disconnect your Strava account:</p>
          <ol>
            <li>Click the Strava icon in the top navigation</li>
            <li>Go to the "Connection" tab</li>
            <li>Click "Manage Strava Connection"</li>
            <li>Revoke access from your Strava settings page</li>
          </ol>
          <p>
            <strong>Note:</strong> Disconnecting will stop automatic syncing of
            new activities, but your existing data will remain in the dashboard.
          </p>
        </>
      ),
    },
    {
      question: "Why is my profile picture not showing?",
      answer: (
        <>
          <p>
            If your picture isn't showing:
          </p>
          <ul>
            <li>Make sure you've uploaded an image (JPG, PNG, or GIF)</li>
            <li>Try refreshing the page</li>
            <li>Check that the file size is under 5MB</li>
            <li>Clear your browser cache if the issue persists</li>
          </ul>
          <p>
            If problems continue, try uploading a different image or contact
            support.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="analytics-page">
      <Sidebar isOpen={sidebarOpen} />

      <div className={containerClass}>
        <header className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">Runstreak Dashboard</h1>
          </div>
          <div className="header-right">
            <div className="header-search-container">
              <img src={iconSearch} alt="Search" className="search-icon-header" />
              <input type="text" placeholder="Search" className="header-search-input" />
            </div>
            <NotificationBell />
            <button className="logout-button" onClick={onLogout}>
              Logout
            </button>
            <ProfilePicture
              profilePictureUrl={profileData?.profile_picture_url}
              firstName={profileData?.first_name}
              lastName={profileData?.last_name}
              email={user?.email}
              size="small"
              showInitials={true}
            />
          </div>
        </header>

        <div className="analytics-content">
          <div className="help-header">
            <h1 className="help-title">Help & FAQ</h1>
            <p className="help-subtitle">
              Find answers to common questions about using the Runstreak Dashboard.
            </p>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h2 className="faq-question">{faq.question}</h2>
                <div className="faq-answer">{faq.answer}</div>
              </div>
            ))}
          </div>

          <div className="help-contact">
            <h2>Still need help?</h2>
            <p>
              Contact us at{" "}
              <a href="mailto:hello@runstreakdashboard.com">
                hello@runstreakdashboard.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
